var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { collection, doc, getDoc, getDocs, query as firestoreQuery, where, setDoc, Timestamp, updateDoc, getFirestore } from 'firebase/firestore';
import { useAuth } from '../auth/AuthContext';
// Collection names
const CLIENTS_COLLECTION = 'clients';
const USER_CLIENT_ACCESS_COLLECTION = 'user_client_access';
/**
 * Hook for client-related Firebase queries and mutations using React Query
 */
export const useClientQueries = () => {
    const { currentUser } = useAuth();
    const queryClient = useQueryClient();
    const db = getFirestore();
    // Query for getting all clients associated with the current user
    const useClientsByUser = () => {
        return useQuery({
            queryKey: ['clients', currentUser === null || currentUser === void 0 ? void 0 : currentUser.uid],
            queryFn: () => __awaiter(void 0, void 0, void 0, function* () {
                if (!currentUser)
                    return [];
                // Find all client access entries for this user
                const accessRef = collection(db, USER_CLIENT_ACCESS_COLLECTION);
                const q = firestoreQuery(accessRef, where('userId', '==', currentUser.uid));
                const querySnapshot = yield getDocs(q);
                // Fetch the associated client profiles
                const clients = [];
                for (const accessDoc of querySnapshot.docs) {
                    const accessData = accessDoc.data();
                    const clientRef = doc(db, CLIENTS_COLLECTION, accessData.clientId);
                    const clientDoc = yield getDoc(clientRef);
                    if (clientDoc.exists()) {
                        clients.push(clientDoc.data());
                    }
                }
                return clients;
            }),
            enabled: !!currentUser
        });
    };
    // Query for getting a specific client by ID
    const useClientById = (clientId) => {
        return useQuery({
            queryKey: ['client', clientId],
            queryFn: () => __awaiter(void 0, void 0, void 0, function* () {
                if (!clientId)
                    return null;
                const clientRef = doc(db, CLIENTS_COLLECTION, clientId);
                const clientDoc = yield getDoc(clientRef);
                if (clientDoc.exists()) {
                    return clientDoc.data();
                }
                return null;
            }),
            enabled: !!clientId
        });
    };
    // Query to check if user has access to any client
    const useHasClientAccess = () => {
        return useQuery({
            queryKey: ['hasClientAccess', currentUser === null || currentUser === void 0 ? void 0 : currentUser.uid],
            queryFn: () => __awaiter(void 0, void 0, void 0, function* () {
                if (!currentUser)
                    return false;
                const accessRef = collection(db, USER_CLIENT_ACCESS_COLLECTION);
                const q = firestoreQuery(accessRef, where('userId', '==', currentUser.uid));
                const querySnapshot = yield getDocs(q);
                return !querySnapshot.empty;
            }),
            enabled: !!currentUser
        });
    };
    // Query to get user's role for a specific client
    const useClientRole = (clientId) => {
        return useQuery({
            queryKey: ['clientRole', currentUser === null || currentUser === void 0 ? void 0 : currentUser.uid, clientId],
            queryFn: () => __awaiter(void 0, void 0, void 0, function* () {
                if (!currentUser || !clientId)
                    return null;
                const accessId = `${currentUser.uid}_${clientId}`;
                const accessRef = doc(db, USER_CLIENT_ACCESS_COLLECTION, accessId);
                const accessDoc = yield getDoc(accessRef);
                if (accessDoc.exists()) {
                    return accessDoc.data().role;
                }
                return null;
            }),
            enabled: !!currentUser && !!clientId
        });
    };
    // Mutation for creating a new client profile
    const useCreateClient = () => {
        return useMutation({
            mutationFn: (data) => __awaiter(void 0, void 0, void 0, function* () {
                if (!currentUser) {
                    throw new Error('You must be logged in to register a client');
                }
                console.log('[CreateClient] Начало создания клиента...');
                // Create a new client document with auto-generated ID
                const clientRef = doc(collection(db, CLIENTS_COLLECTION));
                const clientId = clientRef.id;
                const now = Timestamp.now();
                console.log('[CreateClient] Подготовка данных клиента:', data.companyName);
                const clientData = Object.assign(Object.assign({ id: clientId }, data), { status: 'active', createdAt: now.toDate(), updatedAt: now.toDate() });
                // Save the client profile
                console.log('[CreateClient] Сохранение профиля клиента...');
                yield setDoc(clientRef, clientData);
                console.log('[CreateClient] Профиль клиента успешно сохранен');
                // Create the user-client access relationship
                const accessId = `${currentUser.uid}_${clientId}`;
                const accessData = {
                    id: accessId,
                    userId: currentUser.uid,
                    clientId,
                    role: 'owner',
                    createdAt: now.toDate()
                };
                console.log('[CreateClient] Сохранение данных связи...');
                yield setDoc(doc(db, USER_CLIENT_ACCESS_COLLECTION, accessId), accessData);
                console.log('[CreateClient] Данные связи успешно сохранены');
                console.log('[CreateClient] Клиент успешно создан:', clientData.companyName);
                return clientData;
            }),
            onSuccess: () => {
                // Invalidate the client list query to trigger a refetch
                queryClient.invalidateQueries({ queryKey: ['clients', currentUser === null || currentUser === void 0 ? void 0 : currentUser.uid] });
                queryClient.invalidateQueries({ queryKey: ['hasClientAccess', currentUser === null || currentUser === void 0 ? void 0 : currentUser.uid] });
            }
        });
    };
    // Mutation for updating a client profile
    const useUpdateClient = () => {
        return useMutation({
            mutationFn: (_a) => __awaiter(void 0, [_a], void 0, function* ({ clientId, data }) {
                const clientRef = doc(db, CLIENTS_COLLECTION, clientId);
                const updateData = Object.assign(Object.assign({}, data), { updatedAt: Timestamp.now().toDate() });
                yield updateDoc(clientRef, updateData);
                return { clientId, data: updateData };
            }),
            onSuccess: (result) => {
                // Invalidate specific client query
                queryClient.invalidateQueries({ queryKey: ['client', result.clientId] });
                queryClient.invalidateQueries({ queryKey: ['clients', currentUser === null || currentUser === void 0 ? void 0 : currentUser.uid] });
            }
        });
    };
    // Mutation for adding a user to a client
    const useAddUserToClient = () => {
        return useMutation({
            mutationFn: (_a) => __awaiter(void 0, [_a], void 0, function* ({ userId, clientId, role = 'user' }) {
                const accessId = `${userId}_${clientId}`;
                const accessRef = doc(db, USER_CLIENT_ACCESS_COLLECTION, accessId);
                // Check if the relationship already exists
                const accessDoc = yield getDoc(accessRef);
                if (accessDoc.exists()) {
                    throw new Error('User already has access to this client');
                }
                const now = Timestamp.now();
                const accessData = {
                    id: accessId,
                    userId,
                    clientId,
                    role,
                    createdAt: now.toDate()
                };
                yield setDoc(accessRef, accessData);
                return accessData;
            })
        });
    };
    return {
        useClientsByUser,
        useClientById,
        useHasClientAccess,
        useClientRole,
        useCreateClient,
        useUpdateClient,
        useAddUserToClient
    };
};
